import React, { useContext, useEffect } from "react";
import { RouteContext } from "../context/RouteContextProvider";
import { OutboundFilterContext } from "../context/OutboundFilterContext";
import {
  getBaseUrl,
  getRefreshIntervalForOutbound,
  isARorSSD,
  parseCapabilitiesResponse,
} from "../util/Util";
import { CapabilityContext } from "../../overview/context/CapabilityContext";
import TabularGridView from "./TabularGridView";
import { VISUAL_GRID_VIEW } from "../util/Constants";
import VisualGridView from "./VisualGridView";
import {
  getDocumentURL,
  getNodeCapability,
} from "../../handler/ParentDataHandler";
import { getRemainingTTL, isRouteCacheValid } from "../util/CacheManager";
import { ROUTE_DATA_CACHE_KEY } from "../util/LocalStorageKey";
import useOutboundConfigContext from "../context/useOutboundConfigContext";
import { AlertContext } from "../context/AlertContext";
import { DWPMetadataContext } from "../context/DWPMetadataContext";
import { FormattedMessage } from "react-intl";
import Link from "@amzn/meridian/link";
import { getDomain, getRegion } from "../../util/Util";
import { getStationCode } from "../../network/NetworkUtil";
import TabularGridViewSSD from "../../outboundSSD/TabularGridViewSSD";
import { isSSD } from "../../outboundSSD/util/Util";
import { PROD } from "../../util/Constants";

const RouteGridView = () => {
  const { alertDispatch } = useContext(AlertContext);
  const { viewModelDWP, dispatchDWP } = useContext(DWPMetadataContext);
  const { viewModel, dispatch } = useContext(RouteContext);
  const { viewModelF, dispatchF } = useContext(OutboundFilterContext);
  const { outboundConfigModel, outboundConfigModelRef, configDispatcher } =
    useOutboundConfigContext();
  const { capability, dispatch: capabilityDispatcher } =
    useContext(CapabilityContext);

  let routeList = viewModel.routeList;

  const filterCluster = (route) => {
    if (viewModelF.cluster.length === 0) {
      return true;
    }
    let result = false;
    viewModelF.cluster.forEach((cluster) => {
      if (route.routeCode.includes(cluster)) {
        result = true;
      }
    });
    return result;
  };
  const filterType = (route) => {
    if (viewModelF.driverType.length === 0) {
      return true;
    }
    let result = false;
    viewModelF.driverType.forEach((driverType) => {
      if (route.routeType.includes(driverType)) {
        result = true;
      }
    });
    return result;
  };
  const filterDeparture = (route) => {
    if (viewModelF.departure.length === 0) {
      return true;
    }
    let result = false;
    viewModelF.departure.forEach((departure) => {
      if (route.routeSLA.includes(departure)) {
        result = true;
      }
    });
    return result;
  };
  const filterWindow = (route) => {
    if (viewModelF.deliveryWindow.length === 0) {
      return true;
    }
    return viewModelF.deliveryWindow.includes(route.deliveryWindow);
  };
  const filterAnchorPoint = (route) => {
    if (viewModelF.sourceNode.length === 0) {
      return true;
    }
    let result = false;
    viewModelF.sourceNode.forEach((sourceNode) => {
      if (route.sourceNode !== null && route.sourceNode.includes(sourceNode)) {
        result = true;
      }
    });
    return result;
  };

  routeList = routeList
    .filter((route) => filterCluster(route))
    .filter((route) => filterType(route))
    .filter((route) => filterDeparture(route))
    .filter((route) => filterAnchorPoint(route))
    .filter((route) => filterWindow(route));

  const getRouteData = () => {
    if (
      Array.isArray(viewModelF.xptNodes) &&
      outboundConfigModelRef.current.stationConfig.initialised
    ) {
      Promise.all([getDocumentURL(), getNodeCapability()]).then((result) => {
        //const result = ["https://logistics.amazon.co.jp", {capability: "\"" + CORE_AMZL + "\""}];
        dispatch({
          type: "LOAD_VIEW",
          data: {
            dispatcher: dispatch,
            filterDispatcher: dispatchF,
            baseUrl: getBaseUrl(result[0]),
            capability: parseCapabilitiesResponse(result[1]),
            xptNodes: viewModelF.xptNodes,
            stationConfig: outboundConfigModelRef.current.stationConfig,
            configDispatcher: configDispatcher,
          },
        });
        capabilityDispatcher({
          type: "GET_CAPABILITIES",
          capabilities: result[1],
        });
      });
    }
  };

  /**
   * This gets invoked on following events:
   *  1. On every page load,
   *  2. Every time xpt filter value is changed,
   *  3. When station config's initialised attribute changes - usually happens when NodeConfig API response is received.
   *  Note: Updates on station config does not re-render useEffect, if initialised attribute value is not changed.
   */
  useEffect(() => {
    getRouteData();
    let firstPollTime = getRefreshIntervalForOutbound(
      outboundConfigModelRef.current.stationConfig,
    );
    const remainingTTL = getRemainingTTL(ROUTE_DATA_CACHE_KEY);
    if (viewModelF.xptNodes && isRouteCacheValid(viewModelF.xptNodes)) {
      firstPollTime = remainingTTL;
    }
    const timeoutHandler = () => {
      getRouteData();
      timeout = setTimeout(
        timeoutHandler,
        getRefreshIntervalForOutbound(
          outboundConfigModelRef.current.stationConfig,
        ),
      );
    };
    let timeout = setTimeout(timeoutHandler, firstPollTime);

    return () => clearTimeout(timeout);
  }, [viewModelF.xptNodes, outboundConfigModel.stationConfig.initialised]);

  // Query DWP Metadata on first load after config is fetched
  useEffect(() => {
    if (outboundConfigModelRef.current.stationConfig.initialised) {
      Promise.all([getDocumentURL(), getNodeCapability()]).then((result) => {
        dispatchDWP({
          type: "LOAD_DWP_INFO",
          data: {
            dispatcher: dispatchDWP,
            baseUrl: getBaseUrl(result[0]),
            capability: parseCapabilitiesResponse(result[1]),
            stationConfig: outboundConfigModelRef.current.stationConfig,
          },
        });
      });
    }
  }, [outboundConfigModelRef.current.stationConfig.initialised]);

  // Show alert for pending plan finalization when DWP Metadata response is received
  useEffect(() => {
    if (viewModelDWP.pendingPlanFinalization) {
      alertDispatch({
        type: "SHOW_NOTIFICATION",
        data: {
          alert: {
            title: (
              <FormattedMessage
                id="outboundAMZL.planFinalizationAlert"
                values={{
                  link: (
                    <Link
                      href={getDispatchPlanningURL()}
                      rel={"noopener noreferrer"}
                      target={"_blank"}
                    >
                      <FormattedMessage
                        id="outboundAMZL.planFinalizationAlert.link"
                        defaultMessage="here"
                      />
                    </Link>
                  ),
                }}
              />
            ),
            type: "warning",
          },
        },
      });
    }
  }, [viewModelDWP.pendingPlanFinalization]);

  // Construct a link to dispatch planning for the site to validate plan finalization status.
  const getDispatchPlanningURL = () => {
    return `https://${getRegion()}.dispatch.${
      getDomain() !== PROD ? "gamma." : ""
    }planning.last-mile.a2z.com/dispatch-planning/${getStationCode()}`;
  };

  return (
    <div>
      {isSSD(capability.data) &&
      outboundConfigModel.stationConfig.tabularGridViewSSDEnabled ? (
        <TabularGridViewSSD routeList={routeList} />
      ) : viewModelF.waveViewType === VISUAL_GRID_VIEW ||
        isARorSSD(capability.data) ? (
        <VisualGridView routeList={routeList} />
      ) : (
        <TabularGridView routeList={routeList} />
      )}
    </div>
  );
};

export default RouteGridView;



// WEBPACK FOOTER //
// ./src/components/outboundAmzl/routegridview/RouteGridView.js